// src/pages/games/MapExplorer.js
// Map Explorer — find Arab countries on an interactive map.
// Country shapes generated from real border coordinates (equirectangular
// projection centered ~25N, viewBox 1000x704).
// Data/audio: reuses the 'countries' collection via useFirebase('arabicFlagGuesser').
// Progress:   stored separately under progress.mapExplorer via useFirebase('mapExplorer').
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import useAnalytics from '../../hooks/useAnalytics';

// ============================================================
// MAP DATA
// ============================================================
const COUNTRIES = [
  // Level 1 — North Africa
  { id: 'MA', en: 'Morocco', ar: 'المغرب', pr: 'al-Maghrib', flag: '🇲🇦', lvl: 1, label: [139, 84],
    aliases: ['morocco'],
    d: 'M151.2,30.4 L197.5,40.0 L203.8,45.5 L210.0,81.4 L180.0,95.2 L163.8,102.1 L116.3,128.3 L116.6,142.7 L60.0,142.1 L83.7,132.5 L105.0,104.9 L102.5,91.1 L118.8,66.2 L130.0,60.7 L140.0,55.2 Z' },
  { id: 'DZ', en: 'Algeria', ar: 'الجزائر', pr: "al-Jazā'ir", flag: '🇩🇿', lvl: 1, label: [255, 116],
    aliases: ['algeria'],
    d: 'M197.5,40.0 L217.5,31.7 L262.5,16.6 L307.5,13.8 L332.5,15.2 L327.5,20.7 L328.8,45.5 L328.1,78.7 L343.8,106.3 L347.5,138.0 L370.0,175.3 L374.9,200.5 L332.5,226.3 L277.5,260.3 L265.0,251.2 L240.0,238.7 L165.0,179.4 L116.6,147.9 L116.6,142.7 L116.3,128.3 L163.8,102.1 L180.0,95.2 L210.0,81.4 L203.8,45.5 Z' },
  { id: 'TN', en: 'Tunisia', ar: 'تونس', pr: 'Tūnis', flag: '🇹🇳', lvl: 1, label: [351, 51],
    aliases: ['tunisia'],
    d: 'M332.5,15.2 L348.8,9.7 L363.8,12.4 L356.2,17.9 L357.5,30.4 L363.8,38.6 L360.0,45.5 L351.2,56.6 L363.8,62.1 L369.5,66.7 L368.8,77.3 L353.8,86.9 L352.5,100.7 L343.8,106.3 L328.1,78.7 L328.8,45.5 L327.5,20.7 Z' },
  { id: 'LY', en: 'Libya', ar: 'ليبيا', pr: 'Lībiyā', flag: '🇱🇾', lvl: 1, label: [431, 134],
    aliases: ['libya'],
    d: 'M369.5,66.7 L390.0,70.4 L413.8,77.3 L421.3,91.1 L441.2,95.2 L462.5,106.3 L476.2,81.4 L500.0,71.8 L523.8,81.4 L537.5,88.3 L537.5,220.8 L537.5,248.4 L524.8,255.3 L450.0,226.3 L424.9,200.9 L402.5,212.5 L374.9,200.5 L370.0,175.3 L347.5,138.0 L343.8,106.3 L352.5,100.7 L353.8,86.9 L368.8,77.3 Z' },
  { id: 'EG', en: 'Egypt', ar: 'مصر', pr: 'Miṣr', flag: '🇪🇬', lvl: 1, label: [610, 150],
    aliases: ['egypt'],
    d: 'M537.5,88.3 L565.0,91.8 L598.8,93.8 L612.5,89.0 L628.8,92.9 L642.5,94.8 L652.5,92.2 L661.2,117.3 L656.2,135.2 L653.1,141.7 L645.0,133.9 L637.5,120.1 L631.9,110.8 L629.4,117.3 L638.8,133.9 L648.1,149.0 L661.2,171.1 L668.8,193.2 L672.5,209.8 L686.2,220.8 L537.5,220.8 Z' },
  { id: 'MR', en: 'Mauritania', ar: 'موريتانيا', pr: 'Mūrītāniyā', flag: '🇲🇷', lvl: 1, label: [85, 255],
    aliases: ['mauritania'],
    d: 'M11.9,237.4 L25.0,274.6 L18.8,302.9 L37.5,295.3 L62.5,302.2 L81.2,310.5 L156.2,310.5 L165.0,179.4 L116.6,147.9 L116.6,175.3 L75.0,175.3 L75.0,200.8 L61.3,207.0 L61.9,229.9 L13.1,229.9 Z' },

  // Level 2 — Levant & Iraq
  { id: 'SY', en: 'Syria', ar: 'سوريا', pr: 'Sūriyā', flag: '🇸🇾', lvl: 2, sm: true, label: [706, 40],
    aliases: ['syria'],
    d: 'M673.8,28.3 L683.1,16.6 L700.0,15.2 L731.2,17.2 L755.0,12.4 L741.2,20.7 L740.0,44.2 L710.0,63.5 L680.0,78.0 L671.9,72.5 L672.5,64.2 L678.8,58.0 L682.5,52.4 L674.6,46.2 L671.9,34.5 Z' },
  { id: 'LB', en: 'Lebanon', ar: 'لبنان', pr: 'Lubnān', flag: '🇱🇧', lvl: 2, sm: true, label: [672, 58],
    aliases: ['lebanon'],
    d: 'M674.6,46.2 L682.5,52.4 L678.8,58.0 L672.5,64.2 L663.8,67.8 L665.0,65.3 L668.8,56.6 L670.6,51.1 Z' },
  { id: 'JO', en: 'Jordan', ar: 'الأردن', pr: 'al-Urdun', flag: '🇯🇴', lvl: 2, sm: true, label: [684, 95],
    aliases: ['jordan'],
    d: 'M671.9,72.5 L680.0,78.0 L710.0,63.5 L715.0,80.7 L693.8,92.5 L700.0,103.5 L687.5,110.4 L675.0,121.4 L662.1,119.4 L662.5,110.4 L668.1,96.6 L669.4,85.6 L669.4,77.3 L670.0,73.8 Z' },
  { id: 'PS', en: 'Palestine', ar: 'فلسطين', pr: 'Filasṭīn', flag: '🇵🇸', lvl: 2, sm: true, label: [665, 84],
    aliases: ['palestine'],
    d: 'M669.4,77.3 L666.2,75.6 L663.1,79.4 L661.9,85.6 L662.5,91.8 L668.1,91.6 L669.4,85.6 Z M652.5,92.2 L656.9,88.3 L656.0,90.0 L653.1,93.6 Z' },
  { id: 'IQ', en: 'Iraq', ar: 'العراق', pr: 'al-ʿIrāq', flag: '🇮🇶', lvl: 2, label: [782, 68],
    aliases: ['iraq'],
    d: 'M751.2,95.2 L783.8,121.4 L806.9,122.8 L814.4,109.7 L825.0,110.7 L831.9,111.1 L821.2,96.6 L817.5,78.7 L801.2,70.4 L792.5,56.6 L800.0,44.2 L792.5,30.4 L785.0,12.4 L777.5,9.9 L755.0,12.4 L741.2,20.7 L740.0,44.2 L710.0,63.5 L715.0,80.7 Z' },

  // Level 3 — Arabian Peninsula
  { id: 'SA', en: 'Saudi Arabia', ar: 'السعودية', pr: 'as-Suʿūdiyya', flag: '🇸🇦', lvl: 3, label: [778, 183],
    aliases: ['saudi arabia', 'saudi'],
    d: 'M661.9,119.4 L675.0,121.4 L687.5,110.4 L700.0,103.5 L693.8,92.5 L715.0,80.7 L751.2,95.2 L783.8,121.4 L806.9,122.8 L821.2,130.7 L831.2,132.5 L841.2,144.9 L852.5,161.5 L860.0,182.9 L866.2,184.9 L870.0,191.8 L882.5,208.4 L915.0,211.1 L920.0,220.8 L912.5,248.4 L875.0,262.2 L818.8,288.4 L792.5,285.7 L765.0,285.7 L758.8,298.1 L747.5,277.4 L731.2,251.2 L713.8,230.5 L710.0,213.9 L693.8,189.1 L686.2,169.7 L672.5,151.8 L660.0,138.0 Z' },
  { id: 'YE', en: 'Yemen', ar: 'اليمن', pr: 'al-Yaman', flag: '🇾🇪', lvl: 3, label: [810, 315],
    aliases: ['yemen'],
    d: 'M758.8,298.1 L763.8,303.6 L765.0,336.7 L767.5,349.8 L787.5,348.5 L812.5,336.7 L838.7,324.3 L862.5,313.3 L877.5,308.4 L888.7,295.3 L883.8,285.7 L875.0,262.2 L818.8,288.4 L792.5,285.7 L765.0,285.7 Z' },
  { id: 'OM', en: 'Oman', ar: 'عُمان', pr: 'ʿUmān', flag: '🇴🇲', lvl: 3, label: [934, 240],
    aliases: ['oman'],
    d: 'M888.7,295.3 L901.2,289.8 L918.8,277.4 L937.5,267.7 L947.5,263.6 L957.5,242.9 L967.5,227.7 L973.1,213.9 L957.5,198.7 L943.8,194.6 L933.8,187.7 L929.4,180.1 L922.5,193.2 L920.0,220.8 L912.5,248.4 L875.0,262.2 L883.8,285.7 Z M926.0,170.4 L927.5,162.8 L930.6,160.8 L929.4,167.0 L927.3,171.1 Z' },
  { id: 'AE', en: 'UAE', ar: 'الإمارات', pr: 'al-Imārāt', flag: '🇦🇪', lvl: 3, sm: true, label: [903, 194],
    aliases: ['united arab emirates', 'uae', 'emirates'],
    d: 'M870.0,191.8 L882.5,191.1 L898.8,191.1 L905.0,187.0 L912.5,179.4 L916.2,175.7 L925.0,168.4 L926.0,170.4 L927.3,171.1 L929.4,175.3 L929.4,180.1 L922.5,193.2 L915.0,211.1 L882.5,208.4 Z' },
  { id: 'QA', en: 'Qatar', ar: 'قطر', pr: 'Qaṭar', flag: '🇶🇦', lvl: 3, sm: true, label: [865, 174],
    aliases: ['qatar'],
    d: 'M860.0,182.9 L859.6,172.5 L861.3,164.2 L867.5,163.5 L870.0,171.1 L869.4,179.4 L866.2,184.9 Z' },
  { id: 'BH', en: 'Bahrain', ar: 'البحرين', pr: 'al-Baḥrayn', flag: '🇧🇭', lvl: 3, sm: true, label: [856, 165],
    aliases: ['bahrain'],
    circles: [[856, 165, 3.5]] },
  { id: 'KW', en: 'Kuwait', ar: 'الكويت', pr: 'al-Kuwayt', flag: '🇰🇼', lvl: 3, sm: true, label: [820, 119],
    aliases: ['kuwait'],
    d: 'M806.9,122.8 L814.4,109.7 L825.0,110.7 L827.5,115.9 L830.0,130.4 L821.2,130.7 Z' },

  // Level 4 — Nile Valley & The Horn
  { id: 'SD', en: 'Sudan', ar: 'السودان', pr: 'as-Sūdān', flag: '🇸🇩', lvl: 4, label: [600, 300],
    aliases: ['sudan'],
    d: 'M537.5,220.8 L686.2,220.8 L688.8,234.6 L690.0,253.9 L707.5,276.0 L686.2,295.3 L681.2,314.6 L663.8,349.1 L651.2,368.5 L648.8,390.5 L593.8,387.8 L547.5,385.0 L527.5,404.3 L518.8,374.0 L500.0,349.1 L497.5,309.1 L511.2,300.8 L524.8,255.3 L537.5,248.4 Z' },
  { id: 'SO', en: 'Somalia', ar: 'الصومال', pr: 'aṣ-Ṣūmāl', flag: '🇸🇴', lvl: 4, label: [821, 436],
    aliases: ['somalia'],
    d: 'M765.6,366.1 L768.8,367.8 L787.5,374.7 L806.2,376.7 L836.3,368.5 L866.0,361.0 L863.7,378.1 L855.0,407.1 L837.5,434.7 L812.5,462.3 L791.2,496.4 L768.8,516.1 L756.2,529.2 L737.5,547.2 L748.8,469.5 L787.5,455.4 L825.0,414.0 L767.5,387.8 Z' },
  { id: 'DJ', en: 'Djibouti', ar: 'جيبوتي', pr: 'Jībūtī', flag: '🇩🇯', lvl: 4, sm: true, label: [757, 364],
    aliases: ['djibouti'],
    d: 'M765.6,366.1 L767.5,360.2 L764.4,353.0 L758.8,353.3 L750.0,365.7 L747.5,372.6 L761.2,373.3 Z' },
  { id: 'KM', en: 'Comoros', ar: 'جزر القمر', pr: 'Juzur al-Qamar', flag: '🇰🇲', lvl: 4, sm: true, label: [766, 686],
    aliases: ['comoros', 'the comoros'],
    circles: [[766.2, 685.9, 5], [780.0, 692.8, 4], [771.9, 694.1, 3.2]] },
];

const LEVELS = [
  { n: 1, name: 'North Africa', arName: 'شمال أفريقيا', icon: '🏜️', vb: '0 0 700 493' },
  { n: 2, name: 'Levant & Iraq', arName: 'بلاد الشام والعراق', icon: '🏛️', vb: '645 0 200 141' },
  { n: 3, name: 'Arabian Peninsula', arName: 'شبه الجزيرة العربية', icon: '🕌', vb: '645 105 355 250' },
  { n: 4, name: 'Nile & The Horn', arName: 'وادي النيل والقرن الأفريقي', icon: '🌊', vb: '460 215 695 489' },
  { n: 5, name: 'The Whole Arab World', arName: 'الوطن العربي كله', icon: '🌍', vb: '0 0 1000 704' },
];

const VB_WORLD = '0 0 1000 704';
const VB_LEVANT = '645 0 200 141';
const VB_GULF = '645 105 355 250';

const GRAY_PATHS = [
  // Africa (non-Arab context + Western Sahara backdrop)
  'M151.2,30.4 L262.5,16.6 L348.8,9.7 L363.8,12.4 L413.8,77.3 L462.5,106.3 L476.2,81.4 L537.5,88.3 L598.8,93.8 L628.8,92.9 L631.9,110.8 L647.5,149.0 L672.5,209.8 L690.0,253.9 L707.5,276.0 L740.0,331.2 L758.8,350.1 L767.5,360.2 L768.8,367.8 L787.5,374.7 L836.3,368.5 L866.0,361.0 L791.2,496.4 L737.5,547.2 L718.8,593.4 L675.0,662.4 L650.0,703.8 L375.0,703.8 L337.5,552.0 L331.2,466.4 L275.0,441.6 L125.0,462.3 L56.2,393.3 L15.0,351.9 L18.8,302.9 L11.9,237.4 L26.2,197.3 L60.0,142.1 L105.0,104.9 L130.0,60.7 Z',
  // Turkey
  'M552.5,0.0 L785.0,0.0 L785.0,12.4 L777.5,9.9 L755.0,12.4 L731.2,17.2 L700.0,15.2 L683.1,16.6 L680.0,24.8 L673.8,28.3 L675.0,15.2 L606.2,23.5 L566.2,17.9 Z',
  // Iran
  'M785.0,0.0 L1000.0,0.0 L1000.0,176.6 L995.0,176.6 L962.5,173.9 L941.2,169.7 L932.5,150.4 L900.0,155.9 L868.8,140.8 L860.0,125.6 L843.8,111.8 L836.3,106.3 L831.9,111.1 L821.2,96.6 L817.5,78.7 L801.2,70.4 L792.5,56.6 L800.0,44.2 L792.5,30.4 L785.0,12.4 Z',
  // Levant/Arabia backdrop (neutral land behind small states)
  'M652.5,92.2 L656.9,88.3 L663.8,67.8 L668.8,56.6 L674.6,46.2 L671.9,34.5 L673.8,28.3 L683.1,16.6 L755.0,12.4 L785.0,12.4 L792.5,30.4 L801.2,70.4 L821.2,96.6 L831.9,111.1 L830.0,130.4 L852.5,161.5 L860.0,182.9 L861.3,164.2 L867.5,163.5 L870.0,171.1 L905.0,187.0 L916.2,175.7 L927.5,162.8 L930.6,160.8 L929.4,180.1 L957.5,198.7 L973.1,213.9 L947.5,263.6 L918.8,277.4 L888.7,295.3 L877.5,308.4 L838.7,324.3 L787.5,348.5 L767.5,349.8 L765.0,336.7 L758.8,298.1 L713.8,230.5 L693.8,189.1 L660.0,138.0 L661.9,119.4 L661.2,117.3 Z',
];

// Simplified flag fills (stripes/triangles/bands)
const FLAG_DEFS = {
  MA: { stripes: [['#c1272d', 1]], star: '#006233' },
  DZ: { vertical: [['#006233', 1], ['#ffffff', 1]] },
  TN: { stripes: [['#e70013', 1]], disc: '#ffffff' },
  LY: { stripes: [['#e70013', 1], ['#000000', 2], ['#239e46', 1]] },
  EG: { stripes: [['#ce1126', 1], ['#ffffff', 1], ['#000000', 1]] },
  MR: { stripes: [['#cd2a3e', 1], ['#006233', 4], ['#cd2a3e', 1]] },
  SD: { stripes: [['#d21034', 1], ['#ffffff', 1], ['#000000', 1]], triangle: '#007229' },
  SO: { stripes: [['#4189dd', 1]], star: '#ffffff' },
  DJ: { stripes: [['#6ab2e7', 1], ['#12ad2b', 1]], triangle: '#ffffff' },
  KM: { stripes: [['#ffd100', 1], ['#ffffff', 1], ['#ce1126', 1], ['#3a75c4', 1]], triangle: '#3d8e33' },
  SA: { stripes: [['#006c35', 1]] },
  YE: { stripes: [['#ce1126', 1], ['#ffffff', 1], ['#000000', 1]] },
  OM: { stripes: [['#ffffff', 1], ['#db161b', 1], ['#008000', 1]], band: '#db161b' },
  AE: { stripes: [['#00732f', 1], ['#ffffff', 1], ['#000000', 1]], band: '#ff0000' },
  QA: { stripes: [['#8a1538', 1]], band: '#ffffff' },
  BH: { stripes: [['#ce1126', 1]], band: '#ffffff' },
  KW: { stripes: [['#007a3d', 1], ['#ffffff', 1], ['#ce1126', 1]], triangle: '#000000' },
  IQ: { stripes: [['#ce1126', 1], ['#ffffff', 1], ['#000000', 1]] },
  SY: { stripes: [['#007a3d', 1], ['#ffffff', 1], ['#000000', 1]] },
  LB: { stripes: [['#ed1c24', 1], ['#ffffff', 2], ['#ed1c24', 1]], cedar: '#00a651' },
  JO: { stripes: [['#000000', 1], ['#ffffff', 1], ['#007a3d', 1]], triangle: '#ce1126' },
  PS: { stripes: [['#000000', 1], ['#ffffff', 1], ['#007a3d', 1]], triangle: '#ce1126' },
};

const STAR_PTS = '0.5,0.22 0.565,0.42 0.78,0.42 0.607,0.55 0.67,0.76 0.5,0.63 0.33,0.76 0.393,0.55 0.22,0.42 0.435,0.42';

const renderFlagDefs = () => (
  <defs>
    {Object.entries(FLAG_DEFS).map(([id, fd]) => {
      const parts = fd.stripes || fd.vertical;
      const total = parts.reduce((s, [, w]) => s + w, 0);
      let acc = 0;
      return (
        <pattern key={id} id={`fpat-${id}`} patternUnits="objectBoundingBox"
          patternContentUnits="objectBoundingBox" width="1" height="1">
          {fd.stripes && fd.stripes.map(([col, w], i) => {
            const y = acc / total; acc += w;
            return <rect key={i} x="0" y={y} width="1" height={w / total + 0.004} fill={col} />;
          })}
          {fd.vertical && fd.vertical.map(([col, w], i) => {
            const x = acc / total; acc += w;
            return <rect key={i} x={x} y="0" width={w / total + 0.004} height="1" fill={col} />;
          })}
          {fd.band && <rect x="0" y="0" width="0.28" height="1" fill={fd.band} />}
          {fd.triangle && <polygon points="0,0 0.4,0.5 0,1" fill={fd.triangle} />}
          {fd.star && <polygon points={STAR_PTS} fill={fd.star} />}
          {fd.disc && <circle cx="0.5" cy="0.5" r="0.16" fill={fd.disc} />}
          {fd.cedar && <polygon points="0.5,0.3 0.63,0.62 0.37,0.62" fill={fd.cedar} />}
        </pattern>
      );
    })}
  </defs>
);

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const countriesFor = (lvl) => (lvl === 5 ? COUNTRIES : COUNTRIES.filter(c => c.lvl === lvl));

// ============================================================
// COMPONENT
// ============================================================
const MapExplorer = () => {
  const { saveSessionData } = useAnalytics();

  // Country data + audio come from the flag guesser's 'countries' collection
  const {
    levelsData,
    isLoading: countriesLoading,
  } = useFirebase('arabicFlagGuesser');

  // Separate progress storage for this game
  const {
    bestScores,
    saveLevelData,
    resetAllProgress,
  } = useFirebase('mapExplorer');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    startBackgroundMusic,
    AudioElements,
  } = useAudio();

  // ---------- State ----------
  const [screen, setScreen] = useState('menu'); // menu | play
  const [levelNum, setLevelNum] = useState(1);
  const [vb, setVb] = useState(VB_WORLD);
  const [queue, setQueue] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [found, setFound] = useState({});
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [firstTry, setFirstTry] = useState(0);
  const [wrongOnQ, setWrongOnQ] = useState(0);
  const [hinted, setHinted] = useState(false);
  const [hintsLeft, setHintsLeft] = useState(2);
  const [flashId, setFlashId] = useState(null);
  const [pulseId, setPulseId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const [complete, setComplete] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [audioMap, setAudioMap] = useState({});

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  const [levelStartTime, setLevelStartTime] = useState(Date.now());

  const audioRef = useRef(null);
  const target = queue[qIndex] || null;
  const vbW = parseFloat(vb.split(' ')[2]);
  const f = vbW / 1000;

  // ---------- Resolve country audio from the countries collection ----------
  useEffect(() => {
    if (!levelsData || countriesLoading) return;
    const byCode = {};
    const byName = {};
    Object.values(levelsData).forEach(docData => {
      if (!docData) return;
      const url = docData.audio?.country?.url || null;
      if (!url) return;
      const code = (docData.countryCode || '').toUpperCase().trim();
      const en = (docData.names?.english || '').toLowerCase().trim();
      if (code) byCode[code] = url;
      if (en) byName[en] = url;
    });
    const resolved = {};
    COUNTRIES.forEach(c => {
      resolved[c.id] = byCode[c.id]
        || c.aliases.map(a => byName[a]).find(Boolean)
        || null;
    });
    setAudioMap(resolved);
  }, [levelsData, countriesLoading]);

  // ---------- Country name audio (native-speaker recordings) ----------
  const playCountryAudio = useCallback((countryId) => {
    const url = audioMap[countryId];
    if (!url) return;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      const audio = new Audio(url);
      audio.volume = 0.6;
      audioRef.current = audio;
      audio.addEventListener('canplaythrough', () => {
        audio.play().catch(e => console.log('Country audio play failed:', e));
      }, { once: true });
      audio.addEventListener('error', (e) => {
        console.log('Country audio failed to load:', url, e);
      }, { once: true });
      audio.load();
    } catch (e) {
      console.log('Error creating country audio:', e);
    }
  }, [audioMap]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const triggerConfetti = useCallback((big = false) => {
    confetti({
      particleCount: big ? 150 : 60,
      origin: { y: 0.6 },
      colors: ['#00c46a', '#00ddff', '#ffd100', '#ff6b9d'],
      gravity: 0.8,
    });
  }, []);

  // ---------- Level locking (level N+1 unlocks when level N has a saved score) ----------
  const isUnlocked = useCallback((n) => {
    return n === 1 || !!bestScores[`level${n - 1}`];
  }, [bestScores]);

  // ---------- Game flow ----------
  const startLevel = useCallback((n) => {
    setLevelNum(n);
    setQueue(shuffle(countriesFor(n)));
    setQIndex(0);
    setFound({});
    setScore(0);
    setStreak(0);
    setBestStreak(0);
    setFirstTry(0);
    setWrongOnQ(0);
    setHinted(false);
    setHintsLeft(2);
    setFlashId(null);
    setPulseId(null);
    setFeedback(null);
    setLocked(false);
    setComplete(null);
    setVb(LEVELS[n - 1].vb);
    setSessionStartTime(Date.now());
    setSessionMistakes([]);
    setLevelStartTime(Date.now());
    setScreen('play');
    startBackgroundMusic();
  }, [startBackgroundMusic]);

  const finishLevel = useCallback((finalScore, finalFirst, finalBestStreak, mistakes, total, lvl, startedAt) => {
    const acc = Math.round((finalFirst / total) * 100);
    const bonus = acc >= 90 ? 300 : acc >= 70 ? 150 : 0;
    const stars = acc >= 90 ? 3 : acc >= 70 ? 2 : 1;
    const totalScore = finalScore + bonus;
    const gameTime = Math.round((Date.now() - startedAt) / 1000);

    setComplete({ score: totalScore, acc, bonus, stars, first: finalFirst, total });
    if (stars === 3) {
      playPerfectSound();
    } else {
      playCorrectSound();
    }
    triggerConfetti(true);

    // Persist best score for this level
    const scoreData = {
      totalScore,
      stars,
      accuracy: acc,
      attempts: total,
      firstTry: finalFirst,
      bestStreak: finalBestStreak,
      gameTime,
    };
    saveLevelData(lvl, scoreData);

    // Analytics session (same shape as other games)
    const sessionData = {
      gameType: 'mapExplorer',
      level: lvl,
      sessionStartTime: startedAt,
      sessionDuration: gameTime,
      totalScore,
      accuracy: acc,
      attempts: total,
      correctAnswers: total, // every country is eventually found
      mistakes,
      starsEarned: stars,
      bestStreak: finalBestStreak,
    };
    saveSessionData(sessionData);
  }, [saveLevelData, saveSessionData, playPerfectSound, playCorrectSound, triggerConfetti]);

  const handleCountryClick = useCallback((c) => {
    if (screen !== 'play' || locked || complete || !target) return;
    const inLevel = levelNum === 5 || c.lvl === levelNum;
    if (!inLevel) return;
    if (found[c.id]) {
      setFeedback({ type: 'info', text: `${c.flag} ${c.en} — already found!` });
      return;
    }

    if (c.id === target.id) {
      const clean = wrongOnQ === 0 && !hinted;
      const pts = clean ? 100 + Math.min(streak * 20, 100) : wrongOnQ <= 1 && !hinted ? 50 : 25;
      const nScore = score + pts;
      const nFirst = firstTry + (clean ? 1 : 0);
      const nStreak = clean ? streak + 1 : 0;
      const nBestStreak = Math.max(bestStreak, nStreak);

      setScore(nScore);
      setFirstTry(nFirst);
      setStreak(nStreak);
      setBestStreak(nBestStreak);
      setFound(prev => ({ ...prev, [c.id]: true }));
      setPulseId(null);
      setLocked(true);
      setFeedback({ type: 'ok', text: `🎉 Correct! ${target.flag} ${target.en} — ${target.ar} (${target.pr})  +${pts}` });

      if (nStreak > 0 && nStreak % 5 === 0) {
        playStreakSound();
      } else {
        playCorrectSound();
      }
      playCountryAudio(target.id);
      triggerConfetti(false);

      setTimeout(() => {
        if (qIndex + 1 >= queue.length) {
          finishLevel(nScore, nFirst, nBestStreak, sessionMistakes, queue.length, levelNum, sessionStartTime);
        } else {
          setQIndex(i => i + 1);
          setWrongOnQ(0);
          setHinted(false);
          setFeedback(null);
          setLocked(false);
        }
      }, 1600);
    } else {
      playIncorrectSound();
      setStreak(0);
      const w = wrongOnQ + 1;
      setWrongOnQ(w);
      setFlashId(c.id);
      setTimeout(() => setFlashId(null), 600);
      setFeedback({ type: 'bad', text: `❌ That's ${c.flag} ${c.en} — keep looking for ${target.en}!` });
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_country_tap',
        attempted: c.en,
        correct: target.en,
        correctArabic: target.ar,
        level: levelNum,
      }]);
      if (w >= 2) setPulseId(target.id);
    }
  }, [screen, locked, complete, target, levelNum, found, wrongOnQ, hinted, streak, score,
      firstTry, bestStreak, qIndex, queue.length, sessionMistakes, sessionStartTime,
      playCorrectSound, playIncorrectSound, playStreakSound, playCountryAudio,
      triggerConfetti, finishLevel]);

  const useHint = useCallback(() => {
    if (hintsLeft <= 0 || locked || !target || complete) return;
    setHintsLeft(h => h - 1);
    setHinted(true);
    setPulseId(target.id);
    setTimeout(() => {
      setPulseId(prev => (prev === target.id && wrongOnQ < 2 ? null : prev));
    }, 2500);
  }, [hintsLeft, locked, target, complete, wrongOnQ]);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    setShowResetConfirmation(false);
  }, [resetAllProgress]);

  // ---------- Colors ----------
  const fillFor = (c) => {
    if (flashId === c.id) return '#f87171';
    if (found[c.id]) return `url(#fpat-${c.id})`;
    const inLevel = levelNum === 5 || c.lvl === levelNum;
    if (screen === 'play' && !inLevel) return '#ece6d8';
    return '#fcd98d';
  };
  const strokeFor = (c) => {
    if (flashId === c.id) return '#b91c1c';
    if (found[c.id]) return '#15803d';
    const inLevel = levelNum === 5 || c.lvl === levelNum;
    if (screen === 'play' && !inLevel) return '#d8d0bf';
    return '#c98f2f';
  };

  const renderCountry = (c) => {
    const inLevel = screen !== 'play' || levelNum === 5 || c.lvl === levelNum;
    const clickable = screen === 'play' && inLevel && !found[c.id] && !locked && !complete;
    const common = {
      fill: fillFor(c),
      stroke: strokeFor(c),
      strokeWidth: (found[c.id] ? 2.6 : 1.4) * Math.max(f, 0.22),
      strokeLinejoin: 'round',
      style: { cursor: clickable ? 'pointer' : 'default', transition: 'fill 0.25s' },
      onClick: () => handleCountryClick(c),
    };
    return (
      <g key={c.id}>
        {c.d && <path d={c.d} {...common} />}
        {c.circles && c.circles.map(([cx, cy, r], i) => (
          <circle key={i} cx={cx} cy={cy} r={r} {...common} />
        ))}
        {/* forgiving hit area around the CURRENT target only (never overlaps) */}
        {target && target.id === c.id && screen === 'play' && !found[c.id] && !complete && (
          <circle cx={c.label[0]} cy={c.label[1]} r={12 * Math.max(f, 0.25)} fill="transparent"
            style={{ cursor: 'pointer' }} onClick={() => handleCountryClick(c)} />
        )}
        {pulseId === c.id && (
          <g className="animate-pulse" style={{ pointerEvents: 'none' }}>
            {c.d && <path d={c.d} fill="#fb923c" opacity="0.75" />}
            {c.circles && c.circles.map(([cx, cy, r], i) => (
              <circle key={i} cx={cx} cy={cy} r={r + 3} fill="#fb923c" opacity="0.75" />
            ))}
          </g>
        )}
      </g>
    );
  };

  const seaLabel = (x, y, text, rotate = 0, size = 13) => (
    <text x={x} y={y} fontSize={size} fontStyle="italic" fill="#5b93bd" textAnchor="middle"
      transform={rotate ? `rotate(${rotate} ${x} ${y})` : undefined} style={{ pointerEvents: 'none' }}>
      {text}
    </text>
  );

  // ---------- Loading ----------
  if (countriesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-100 via-purple-50 to-amber-50 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full text-center">
          <div className="text-6xl mb-4 animate-bounce">🗺️</div>
          <div className="text-emerald-600 text-2xl font-bold animate-pulse">Loading the map...</div>
        </div>
        <AudioElements />
      </div>
    );
  }

  // ================= MENU =================
  if (screen === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-100 via-purple-50 to-amber-50 p-4 md:p-8 font-sans">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-start mb-2">
            <Link to="/" className="px-4 py-2 bg-white/90 rounded-2xl border-2 border-gray-300 font-bold text-gray-600 hover:border-gray-400">
              🏠 Home
            </Link>
          </div>
          <div className="text-7xl mb-3">🗺️</div>
          <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent mb-1">
            Map Explorer
          </h1>
          <p className="text-2xl font-bold text-gray-600 mb-1" dir="rtl">مُستكشف الخريطة</p>
          <p className="text-gray-500 mb-6">Find the Arab countries on the map!</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {LEVELS.map(l => {
              const unlocked = isUnlocked(l.n);
              const best = bestScores[`level${l.n}`];
              return (
                <button key={l.n} onClick={() => unlocked && startLevel(l.n)} disabled={!unlocked}
                  className={`p-5 rounded-3xl border-4 text-left transition-all ${l.n === 5 ? 'md:col-span-2' : ''} ${
                    unlocked
                      ? 'bg-white/90 border-emerald-200 hover:border-emerald-400 hover:shadow-xl hover:scale-[1.02]'
                      : 'bg-gray-100/80 border-gray-200 opacity-60 cursor-not-allowed'}`}>
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{unlocked ? l.icon : '🔒'}</div>
                    <div className="flex-1">
                      <div className="font-extrabold text-xl text-gray-800">Level {l.n}: {l.name}</div>
                      <div className="text-gray-500 font-bold" dir="rtl">{l.arName}</div>
                      <div className="text-sm text-emerald-600 font-bold mt-1">
                        {countriesFor(l.n).length} countries to find
                        {best && (
                          <span className="ml-2 text-amber-500">
                            {'⭐'.repeat(best.bestStars || best.stars || 0)} Best: {best.bestScore || best.totalScore || 0}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-3xl">{unlocked ? '▶️' : ''}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex justify-center gap-3 flex-wrap">
            <button onClick={() => setShowHow(true)}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all">
              📖 How to Play
            </button>
            <button onClick={() => setShowResetConfirmation(true)}
              className="px-8 py-3 bg-white/90 border-2 border-gray-300 text-gray-500 rounded-2xl font-bold text-lg hover:border-red-300 hover:text-red-500 transition-all">
              🔄 Reset Progress
            </button>
          </div>
        </div>

        {showHow && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowHow(false)}>
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full border-4 border-purple-300" onClick={e => e.stopPropagation()}>
              <h2 className="text-2xl font-extrabold text-purple-700 mb-4">📖 How to Play</h2>
              <ul className="space-y-3 text-gray-700 font-semibold">
                <li>🎯 Read (or listen to) the country name in Arabic</li>
                <li>👆 Tap that country on the map</li>
                <li>🚩 Found countries fill up with their flag!</li>
                <li>⭐ First-try answers earn the most points — build a streak!</li>
                <li>💡 Stuck? Use a hint to make the country glow (2 per level)</li>
                <li>🔍 Use the zoom buttons to find the small countries</li>
              </ul>
              <button onClick={() => setShowHow(false)}
                className="mt-6 w-full py-3 bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-2xl font-bold text-lg">
                Let's Explore!
              </button>
            </div>
          </div>
        )}

        {showResetConfirmation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full border-4 border-red-300 text-center">
              <div className="text-5xl mb-3">⚠️</div>
              <h2 className="text-2xl font-extrabold text-red-600 mb-2">Reset all progress?</h2>
              <p className="text-gray-600 font-semibold mb-6">This will erase your stars and best scores for Map Explorer. This cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowResetConfirmation(false)}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-2xl font-bold">
                  Cancel
                </button>
                <button onClick={confirmResetProgress}
                  className="flex-1 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold">
                  Yes, Reset
                </button>
              </div>
            </div>
          </div>
        )}
        <AudioElements />
      </div>
    );
  }

  // ================= PLAY =================
  const level = LEVELS[levelNum - 1];
  const foundCount = Object.keys(found).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-purple-50 to-amber-50 p-3 md:p-6 font-sans">
      <div className="max-w-4xl mx-auto">

        {/* Top bar */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <button onClick={() => setScreen('menu')}
            className="px-4 py-2 bg-white/90 rounded-2xl border-2 border-gray-300 font-bold text-gray-600 hover:border-gray-400">
            ← Menu
          </button>
          <div className="font-extrabold text-gray-700">{level.icon} Level {levelNum}: {level.name}</div>
          <div className="flex gap-2 text-sm font-extrabold">
            <div className="px-3 py-2 bg-gradient-to-br from-blue-400 to-blue-600 text-white rounded-xl">🎯 {score}</div>
            <div className="px-3 py-2 bg-gradient-to-br from-orange-400 to-red-500 text-white rounded-xl">🔥 {streak}</div>
            <div className="px-3 py-2 bg-gradient-to-br from-emerald-400 to-emerald-600 text-white rounded-xl">🗺️ {foundCount}/{queue.length}</div>
          </div>
        </div>

        {/* Prompt card */}
        {target && !complete && (
          <div className="bg-white/95 rounded-3xl border-4 border-emerald-300 p-4 mb-3 text-center shadow-lg">
            <div className="text-sm font-bold text-gray-400 uppercase tracking-wide">Find this country</div>
            <div className="flex items-center justify-center gap-4 flex-wrap mt-1">
              <div className="text-3xl md:text-4xl font-extrabold text-gray-800" dir="rtl">{target.ar}</div>
              <div className="text-xl md:text-2xl font-bold text-emerald-700">{target.en}</div>
              <div className="text-gray-400 italic">({target.pr})</div>
              {audioMap[target.id] && (
                <button onClick={() => playCountryAudio(target.id)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold hover:scale-105 transition-all">
                  🔊 Listen
                </button>
              )}
              <button onClick={useHint} disabled={hintsLeft <= 0}
                className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-2xl font-bold hover:scale-105 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                💡 Hint ({hintsLeft})
              </button>
            </div>
          </div>
        )}

        {/* Zoom chips */}
        <div className="flex gap-2 mb-2 flex-wrap items-center">
          <button onClick={() => setVb(level.vb)}
            className={`px-3 py-1.5 rounded-xl text-sm font-bold border-2 ${vb === level.vb ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'}`}>
            🎯 Region
          </button>
          <button onClick={() => setVb(VB_WORLD)}
            className={`px-3 py-1.5 rounded-xl text-sm font-bold border-2 ${vb === VB_WORLD ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'}`}>
            🌍 World
          </button>
          {levelNum === 5 && (
            <>
              <button onClick={() => setVb(VB_LEVANT)}
                className={`px-3 py-1.5 rounded-xl text-sm font-bold border-2 ${vb === VB_LEVANT ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                🔍 Levant
              </button>
              <button onClick={() => setVb(VB_GULF)}
                className={`px-3 py-1.5 rounded-xl text-sm font-bold border-2 ${vb === VB_GULF ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                🔍 Gulf
              </button>
              {target && target.sm && vb === VB_WORLD && (
                <span className="text-sm font-bold text-amber-600 animate-pulse">← Tip: zoom in for small countries!</span>
              )}
            </>
          )}
        </div>

        {/* Map */}
        <div className="bg-white rounded-3xl border-4 border-sky-200 overflow-hidden shadow-xl relative">
          <svg viewBox={vb} className="w-full h-auto block select-none" style={{ background: '#aed7f2' }}>
            {renderFlagDefs()}
            {GRAY_PATHS.map((d, i) => (
              <path key={i} d={d} fill="#e7e2d5" stroke="#d3ccbc" strokeWidth={1 * Math.max(f, 0.22)} />
            ))}

            {/* Sea labels (world view only) */}
            {f > 0.9 && (
              <g>
                {seaLabel(400, 45, 'Mediterranean Sea')}
                {seaLabel(28, 250, 'Atlantic Ocean', -90)}
                {seaLabel(696, 218, 'Red Sea', 55)}
                {seaLabel(875, 142, 'Arabian Gulf', 28, 11)}
                {seaLabel(820, 356, 'Gulf of Aden', 0, 9)}
                {seaLabel(945, 340, 'Arabian Sea')}
                {seaLabel(900, 600, 'Indian Ocean')}
              </g>
            )}

            {/* Countries */}
            {COUNTRIES.map(renderCountry)}

            {/* Labels for found countries */}
            {COUNTRIES.filter(c => found[c.id]).map(c => {
              if (c.sm && f > 0.55) return null;
              return (
                <text key={`lbl-${c.id}`} x={c.label[0]} y={c.label[1]} textAnchor="middle"
                  fontSize={(c.sm ? 8 : 12) * Math.max(f, 0.2)} fontWeight="800" fill="#14532d"
                  stroke="#ffffff" strokeWidth={0.35 * Math.max(f, 0.2)} paintOrder="stroke"
                  style={{ pointerEvents: 'none' }}>
                  {c.flag} {c.en}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Feedback banner */}
        <div className="mt-3 min-h-[3.5rem]">
          {feedback && (
            <div className={`p-3 rounded-2xl border-4 text-center font-extrabold text-lg ${
              feedback.type === 'ok' ? 'bg-green-100 border-green-300 text-green-700' :
              feedback.type === 'bad' ? 'bg-red-100 border-red-300 text-red-700' :
              'bg-sky-100 border-sky-300 text-sky-700'}`}>
              {feedback.text}
            </div>
          )}
        </div>

        {/* Level complete modal */}
        {complete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full border-4 border-emerald-300 text-center">
              <div className="text-6xl mb-2">🏆</div>
              <h2 className="text-3xl font-extrabold text-emerald-700 mb-3">Level Complete!</h2>
              <div className="flex justify-center gap-2 text-5xl mb-4">
                {[1, 2, 3].map(s => (
                  <span key={s} className={complete.stars >= s ? 'animate-bounce' : 'opacity-25 grayscale'}
                    style={{ animationDelay: `${s * 0.15}s` }}>⭐</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 text-left font-bold text-gray-700 mb-5">
                <div className="bg-blue-50 rounded-2xl p-3">🎯 Score<br /><span className="text-2xl text-blue-600">{complete.score}</span></div>
                <div className="bg-green-50 rounded-2xl p-3">✅ Accuracy<br /><span className="text-2xl text-green-600">{complete.acc}%</span></div>
                <div className="bg-purple-50 rounded-2xl p-3">🥇 First try<br /><span className="text-2xl text-purple-600">{complete.first}/{complete.total}</span></div>
                <div className="bg-amber-50 rounded-2xl p-3">🎁 Bonus<br /><span className="text-2xl text-amber-600">+{complete.bonus}</span></div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => startLevel(levelNum)}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold">
                  🔄 Replay
                </button>
                {levelNum < 5 ? (
                  <button onClick={() => startLevel(levelNum + 1)}
                    className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-2xl font-bold">
                    Next Level ➡️
                  </button>
                ) : (
                  <button onClick={() => setScreen('menu')}
                    className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-2xl font-bold">
                    🏠 Menu
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <AudioElements />
    </div>
  );
};

export default MapExplorer;